import { Typography } from '@mui/material';
import React, { useState } from 'react';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';
import Header from '../../components/layout/Header';
import PrimaryBox from '../../components/layout/PrimaryBox';
import SitesValidationSummaryTable from '../../components/tables/operations/validation/SitesValidationTable';
import ReportsValidationTable from '../../components/tables/operations/validation/ValidationAbsencesTable';
import CustomTabs from '../../components/ui/CustomTabs';
import { localStorageKeys, systemConfigKeyEnum } from '../../constants';
import { useValidationSitesSummary } from '../../hooks/queries/useActivitiesQuery';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getTomorrowDate } from '../../utils/dates';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';

const ValidationPage: React.FC = () => {
    const [currentDate, setCurrentDate] = useState(new Date(getItemFromLocalStorage(localStorageKeys.DATE) || getTomorrowDate()));

    const [selectedTab, setSelectedTab] = useState(parseInt(getItemFromLocalStorage(localStorageKeys.TAB.VALIDATION) || '0'));

    const { user } = useAuth();
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    usePageTitle(capitalizeFirstLetter(t('validation')));

    const {
        data: dataSites,
        isLoading: isSitesLoading,
        refetch: refetchSites,
    } = useValidationSitesSummary({ queries: { date: currentDate.toISOString() } });

    const handleDateChange = (newDate: string) => {
        setCurrentDate(new Date(newDate));
        storeItemToLocalStorage(localStorageKeys.DATE, newDate);
    };

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
        storeItemToLocalStorage(localStorageKeys.TAB.VALIDATION, newValue.toString());
    };

    return (
        <PrimaryBox>
            <Header
                title={t('operations')}
                breadcrumbs={[{ label: capitalizeFirstLetter(t('validation')) }]}
                subTitle={capitalizeFirstLetter(t('validation'))}
                initialDate={currentDate}
                onDateChange={(newDate) => handleDateChange(newDate.toISOString())}
            >
                <Typography
                    variant="h5"
                    fontWeight={'bold'}
                    noWrap
                    color={(dataSites?.reduce((acc, site) => acc + (site.totalDailyPrice || 0), 0) || 0) > 0 ? 'success' : 'textDisabled'}
                >
                    <CountUp
                        end={dataSites?.reduce((acc, site) => acc + (site.totalDailyPrice || 0), 0) || 0}
                        duration={2}
                        separator=" "
                        decimals={2}
                        decimal=","
                        preserveValue
                        suffix={` ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                        prefix={`${capitalizeFirstLetter(t('expenses'))}${t('separator.colon')}`}
                    />
                </Typography>
            </Header>
            <CustomTabs selectedTab={selectedTab} handleTabChange={handleTabChange} tabLabels={[t('sites'), t('users')]} />
            {selectedTab === 0 && (
                <SitesValidationSummaryTable data={dataSites || []} isLoading={isSitesLoading} currentDate={currentDate} refetch={refetchSites} />
            )}
            {selectedTab === 1 && <ReportsValidationTable currentDate={currentDate} />}
        </PrimaryBox>
    );
};

export default ValidationPage;
